import Image from "next/image";
import Link from "next/link";
import Tealium from "@4tn/webx-analytics";
import { styled } from "@mui/material/styles";
import IconButton from "@common/Buttons/IconButton";
import { BUTTON_SIZE, VARIANT } from "@constants/button";
import { CONTENT_LANGUAGE, IconEnum } from "@constants/consts";
import { colors, fonts } from "@constants/cssVariables";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import getPageCategory, { getRouteSlug } from "@utils/common/getPageCategory";

export const playButtonTestId = "linkListItemPlayButton";

const LinkListItemContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  maxHeight: 76,
}));

const LinkListItemImageContainer = styled("div")(() => ({
  position: "relative",
  width: 115,
  minWidth: 115,
  height: 76,
}));

const LinkListItemImage = styled(Image)(() => ({
  objectFit: "cover",
  borderRadius: 6,
}));

const PlayIcon = styled(IconButton)(() => ({
  position: "absolute",
  left: 85,
  top: 46,
  zIndex: 1,
}));

const LinkListItemTitleContainer = styled("div")(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 0, 3, 0),
  [theme.breakpoints.up("md")]: {
    alignItems: "center",
    padding: theme.spacing(1, 0),
  },
}));

const LinkListItemTitle = styled("p")(({ theme }) => ({
  font: fonts.header6,
  color: colors.neutral80,
  width: "100%",
  maxHeight: 76,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

interface LinkListItemProps {
  id: string;
  url: string;
  title: string;
  imageUrl: string;
  collectionTitle: string;
  hasVideo?: boolean;
}

const LinkListItem: React.FC<LinkListItemProps> = ({ id, url, title, imageUrl, collectionTitle, hasVideo = false }) => {
  // Internal links (article links) take on the following structure /category/article-slug
  // e.g. "/entertainment/pijnlijke-confrontatie-voor-roxeanne-met-rachel"
  // Therefore, if the link has a regular structure (starting with https protocol), it is an external one
  const additionalExternalLinkProps = url.startsWith("https") ? { rel: "noopener noreferrer", target: "_blank" } : {};

  const trackClick = () => {
    const pageCategory = getPageCategory(url);

    Tealium.link({
      event_name: TEALIUM_EVENT_NAME.CONTENT_LISTING_CLICK,
      event_category: TEALIUM_EVENT_CATEGORY.CONTENT_LISTING,
      event_label: collectionTitle,
      content_id: getRouteSlug(url, pageCategory),
      content_language: CONTENT_LANGUAGE,
      content_name: title,
      content_type: pageCategory,
    });
  };

  return (
    <Link href={url} onClick={trackClick} {...additionalExternalLinkProps}>
      <LinkListItemContainer>
        <LinkListItemImageContainer>
          {hasVideo && (
            <PlayIcon
              icon={IconEnum.PLAY_ICON_FILLED}
              size={BUTTON_SIZE.EXTRA_SMALL}
              variant={VARIANT.PRIMARY}
              color={colors.neutral0}
              isNonInteractive
              data-testid={playButtonTestId}
            />
          )}
          <LinkListItemImage src={imageUrl} alt={title} fill sizes="(max-width: 900px) 33vw, 10vw" />
        </LinkListItemImageContainer>
        <LinkListItemTitleContainer>
          <LinkListItemTitle>{title}</LinkListItemTitle>
        </LinkListItemTitleContainer>
      </LinkListItemContainer>
    </Link>
  );
};

export default LinkListItem;
